// Anthropic direct API setup wizard — flow:
//   1) Prompt API key ẩn (password input, format sk-ant-api03-...)
//   2) Verify GET https://api.anthropic.com/v1/models với x-api-key header
//   3) Smart picker: auto-pick nếu chỉ 1 claude model, prompt nếu nhiều
//
// Output: { apiKey, baseUrl, model } cho phase 06 write settings.json.
//
// KHÁC LLMLite:
//   - Header auth: x-api-key (Anthropic standard) thay vì Authorization: Bearer
//   - Bắt buộc anthropic-version: 2023-06-01 (API version header required)
//   - Base URL hardcode https://api.anthropic.com (Anthropic 1 endpoint chính thức)
//   - Settings.json output dùng ANTHROPIC_API_KEY (KHÔNG phải ANTHROPIC_AUTH_TOKEN)
import { passwordOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";

export interface AnthropicConfig {
  apiKey: string;
  baseUrl: string;
  model: string;
  // Model key được phép dùng (từ /v1/models).
  availableModels: string[];
}

// Anthropic official endpoint. KHÔNG cho user override (decision Q1: keep simple).
// User cần proxy đặc biệt → edit settings.json tay sau init.
const ANTHROPIC_BASE_URL = "https://api.anthropic.com";

// API version bắt buộc theo Anthropic spec — endpoint reject nếu thiếu.
// 2023-06-01 là version stable hiện tại (sẽ phải update nếu Anthropic bump).
const ANTHROPIC_API_VERSION = "2023-06-01";

// 10s timeout fetch — đủ cho list models (response < 2KB).
const FETCH_TIMEOUT_MS = 10_000;

// Trả "sk-ant...XXXX" để log không expose full token nhưng vẫn distinguishable.
export function maskAnthropicKey(key: string): string {
  if (key.length <= 12) return "sk-ant-***";
  return `${key.slice(0, 7)}...${key.slice(-4)}`;
}

// Anthropic key format: sk-ant-api03-... (108 ký tự). Soft validate prefix
// để catch typo nhưng không strict length (Anthropic có thể bump format).
function validateAnthropicKeyFormat(key: string): true | string {
  const trimmed = key.trim();
  if (trimmed.length === 0) return "API key bắt buộc";
  if (!trimmed.startsWith("sk-ant-")) {
    return "Anthropic API key thường bắt đầu bằng 'sk-ant-' (lấy từ console.anthropic.com).";
  }
  return true;
}

/** Cờ điền sẵn cho luồng non-interactive (T1). Key CHỈ đến từ stdin, không từ argv. */
export interface AnthropicSetupOptions {
  /** Đã đọc từ stdin qua `--anthropic-api-key-stdin`. */
  apiKey?: string;
  /** Từ `--anthropic-model`. */
  model?: string;
}

const KEY_FLAG_SPEC = {
  flag: "--anthropic-api-key-stdin",
  command: "ai setup",
  hint: 'echo "$KEY" | avatar ai setup --non-interactive --ai-provider anthropic --anthropic-api-key-stdin …',
} as const;

const MODEL_FLAG_SPEC = {
  flag: "--anthropic-model",
  command: "ai setup",
  hint: "--anthropic-model claude-sonnet-4-5",
} as const;

// Prompt ẩn — `password` không echo input, hiển thị `*`.
async function promptAnthropicKeyHidden(provided?: string): Promise<string> {
  const key = await passwordOrFlag(provided, KEY_FLAG_SPEC, {
    message: "Anthropic API key (sk-ant-..., ẩn input):",
    mask: "*",
    validate: validateAnthropicKeyFormat,
  });
  // Key qua stdin KHÔNG đi qua `validate` của inquirer ⇒ kiểm tay, nếu không
  // key sai định dạng chỉ lộ ra ở lỗi HTTP 401 khó hiểu tận tầng dưới.
  const verdict = validateAnthropicKeyFormat(key);
  if (verdict !== true) throw new Error(verdict);
  return key;
}

// Shape response /v1/models theo Anthropic spec — chỉ trust field `id`.
interface AnthropicModelsResponse {
  data?: Array<{ id?: unknown; type?: unknown }>;
}

// Fetch danh sách model từ Anthropic /v1/models.
// Headers: x-api-key + anthropic-version (KHÔNG phải Authorization: Bearer).
export async function fetchAnthropicModels(apiKey: string): Promise<string[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${ANTHROPIC_BASE_URL}/v1/models`, {
      method: "GET",
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": ANTHROPIC_API_VERSION,
        Accept: "application/json",
      },
      signal: controller.signal,
    });

    if (res.status === 401) {
      throw new Error("API key invalid (HTTP 401). Check key trên console.anthropic.com.");
    }
    if (res.status === 403) {
      throw new Error(
        "API key bị reject (HTTP 403). Key có thể đã bị revoke hoặc thiếu permission.",
      );
    }
    if (res.status === 429) {
      throw new Error("Rate limit (HTTP 429). Chờ vài giây rồi thử lại.");
    }
    if (!res.ok) {
      throw new Error(`Fetch models thất bại (HTTP ${res.status}).`);
    }

    const json = (await res.json()) as AnthropicModelsResponse;
    const models = (json.data || [])
      .map((m) => (typeof m.id === "string" ? m.id : null))
      .filter((id): id is string => id !== null);

    if (models.length === 0) {
      throw new Error("Anthropic trả về list rỗng. Liên hệ support hoặc check account.");
    }
    return models;
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new Error(`Connect ${ANTHROPIC_BASE_URL} timeout sau ${FETCH_TIMEOUT_MS / 1000}s.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Smart picker: auto-pick nếu chỉ 1 model, prompt nếu nhiều.
// Anthropic /v1/models trả tất cả claude-* models account access được — không cần
// filter substring như LLMLite (LLMLite list có cả minimax, gpt, gemini...).
export async function promptAnthropicModelChoice(
  models: string[],
  preset?: string,
): Promise<string> {
  // Cờ chỉ định model: kiểm nó CÓ THẬT trên endpoint trước khi ghi vào settings.
  // Bỏ kiểm thì typo model chỉ lộ ra khi Claude Code gọi API và fail khó hiểu.
  if (preset !== undefined && preset !== "") {
    if (!models.includes(preset)) {
      throw new Error(
        `Model "${preset}" không có trên endpoint. Model khả dụng: ${models.join(", ")}`,
      );
    }
    return preset;
  }

  if (models.length === 1) {
    // length === 1 đã guard → models[0] non-null.
    const only = models[0]!;
    log.info(`Auto-pick model: ${only} (chỉ 1 model available)`);
    return only;
  }

  // Sort: Opus preferred, sau đó Sonnet, Haiku. Default top option.
  const sorted = [...models].sort((a, b) => {
    const score = (m: string) => {
      const lower = m.toLowerCase();
      if (lower.includes("opus")) return 0;
      if (lower.includes("sonnet")) return 1;
      if (lower.includes("haiku")) return 2;
      return 3;
    };
    return score(a) - score(b);
  });

  return await selectOrFlag(preset, MODEL_FLAG_SPEC, {
    message: "Chọn model mặc định cho project:",
    choices: sorted.map((m) => ({ name: m, value: m })),
  });
}

// Main wizard. Orchestrate 3 step (KHÔNG có baseUrl prompt — hardcode endpoint).
export async function setupAnthropicApiKeyAndModel(
  options: AnthropicSetupOptions = {},
): Promise<AnthropicConfig> {
  const apiKey = await promptAnthropicKeyHidden(options.apiKey);

  log.info(`Verify key (${maskAnthropicKey(apiKey)}) qua ${ANTHROPIC_BASE_URL}/v1/models...`);
  const models = await fetchAnthropicModels(apiKey);
  log.success(`Endpoint OK — ${models.length} models available`);

  const model = await promptAnthropicModelChoice(models, options.model);
  return { apiKey, baseUrl: ANTHROPIC_BASE_URL, model, availableModels: models };
}
